import { createSelector, Selector } from '@reduxjs/toolkit'
import { isNil } from 'lodash-es'
import { DealHistorySubscriptionIdentifier, DealStatus, DealSubscriptionIdentifier } from 'api'
import { createDealDetailsSelector } from './dealSelectors'
import { GlobalState } from 'store'
import { canServiceTradeSelector } from './permissionSelectors'
import { isTradingGatewayUserSelector } from './userSelectors'
import { currentOrganisationIdSelector } from './adminSelectors'
import { dateLikeCompareDescendingFn } from '../utils'

export const MID_TRADE_VIEW_INCOMING = 'incoming'
export const MID_TRADE_VIEW_INITIATED = 'initiated'
export const MID_TRADE_VIEW_HISTORICAL = 'history'

const views = [MID_TRADE_VIEW_INCOMING, MID_TRADE_VIEW_INITIATED, MID_TRADE_VIEW_HISTORICAL]

const createMidTradeEventsDetailsSelector = (forcedIsServicingInitiator?: boolean) =>
  createSelector(createDealDetailsSelector([DealSubscriptionIdentifier.MID_TRADE_EVENT], forcedIsServicingInitiator), allMidTrades =>
    allMidTrades.filter(
      midTrade => !(midTrade.deal.status === DealStatus.SERVICING_WITH_INITIATOR && !midTrade.deal.servicingDetails?.isActive)
    )
  )

const initiatedMidTradeEventsDetailsSelector = createSelector(
  [createMidTradeEventsDetailsSelector(), createMidTradeEventsDetailsSelector(true), currentOrganisationIdSelector],
  (dealDetails, initiatedDealDetails, currentOrganisationId) =>
    !currentOrganisationId ? initiatedDealDetails : dealDetails.filter(dealDetail => dealDetail.isServicingInitiator)
)

const incomingMidTradeEventsDetailsSelector = createSelector(
  [createMidTradeEventsDetailsSelector(), createMidTradeEventsDetailsSelector(false), currentOrganisationIdSelector],
  (dealDetails, incomingDealDetails, currentOrganisationId) =>
    !currentOrganisationId ? incomingDealDetails : dealDetails.filter(dealDetail => !dealDetail.isServicingInitiator)
)

const historicalMidTradeEventsDetailsSelector = createDealDetailsSelector([DealHistorySubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS])

export const midTradeEventsDetailsViewSelector = createSelector(
  [
    createMidTradeEventsDetailsSelector(),
    initiatedMidTradeEventsDetailsSelector,
    incomingMidTradeEventsDetailsSelector,
    historicalMidTradeEventsDetailsSelector,
    (_, view: string) => view
  ],
  (allMidTrades, initiatedMidTrades, incomingMidTrades, historicalMidTrades, view) => {
    if (isNil(view) || !views.includes(view)) {
      return allMidTrades
    }

    switch (view) {
      case MID_TRADE_VIEW_INITIATED:
        return initiatedMidTrades
      case MID_TRADE_VIEW_INCOMING:
        return incomingMidTrades
      case MID_TRADE_VIEW_HISTORICAL:
        return historicalMidTrades
      default:
        throw new Error('Unsupported mid trade view')
    }
  }
)

export const sortedMidTradeEventsDetailsViewSelector = createSelector(midTradeEventsDetailsViewSelector, midTrades =>
  midTrades.sort((midTrade1, midTrade2) => dateLikeCompareDescendingFn(midTrade1.deal.lastUpdatedAt, midTrade2.deal.lastUpdatedAt))
)

export const midTradeEventsDetailsCountSelector: Selector<GlobalState, number> = createSelector(
  createMidTradeEventsDetailsSelector(),
  dealDetails => dealDetails.length
)

export const initiatedMidTradeEventsDetailsCountSelector: Selector<GlobalState, number> = createSelector(
  [initiatedMidTradeEventsDetailsSelector],
  dealDetails => dealDetails.length
)

export const incomingMidTradeEventsDetailsCountSelector: Selector<GlobalState, number> = createSelector(
  [incomingMidTradeEventsDetailsSelector],
  dealDetails => dealDetails.length
)

export const historicalMidTradeEventsDetailsCountSelector: Selector<GlobalState, number> = createSelector(
  historicalMidTradeEventsDetailsSelector,
  dealDetails => dealDetails.length
)

export const canUploadMidTradeEventsSelector: Selector<GlobalState, boolean> = createSelector(
  canServiceTradeSelector,
  isTradingGatewayUserSelector,
  (canServiceTrades, isTradingUser) => canServiceTrades && isTradingUser
)
